/**
 * HelpHint / HelpText — the two reusable help affordances for the humanized
 * pass designer (§2.3). HelpHint is a small ⓘ info button that reveals an
 * accessible tooltip (role="tooltip", keyboard-focusable, dismiss on blur/Escape);
 * HelpText is the persistent muted inline line that replaces every ad-hoc
 * <p className="text-xs text-slate-500"> help paragraph. Presentational only —
 * no PassDesign state, no network.
 */
import { useId, useState } from 'react';
import type { ReactNode } from 'react';

export interface HelpHintProps {
  text: string;
  example?: string;
  /** aria-label for the info button; defaults to "Súgó". */
  label?: string;
}

export function HelpHint({ text, example, label }: HelpHintProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const tipId = useId();

  return (
    // Mouse + Escape handlers live on the WRAPPER (not the button) so the tooltip —
    // a child of this span — stays within the hover region and is reachable by the
    // pointer (WCAG 1.4.13 hoverable), and Escape dismisses however the tip opened.
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label={label ?? 'Súgó'}
        aria-describedby={open ? tipId : undefined}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold text-slate-500 ring-1 ring-slate-300 hover:text-slate-700"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ⓘ
      </button>
      {open ? (
        <span
          id={tipId}
          role="tooltip"
          className="absolute left-5 top-0 z-10 w-56 rounded-md bg-slate-800 p-2 text-xs font-normal text-white shadow-lg"
        >
          {text}
          {example !== undefined ? (
            <span className="mt-1 block text-slate-300">Példa: {example}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

export function HelpText({ children }: { children: ReactNode }): JSX.Element {
  return <p className="text-xs text-slate-500">{children}</p>;
}
